import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { consts } from '../../../../../consts';
import { ActualSelected, LocalDB } from '../../../../../shared/utils';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';
import { ConboxDialogComponent } from '../../../../../shared/components/add-file/dialog-conbox.component';
import { InvestmentPropertyModel } from '../../../../../modules/investment/property/investment-property.model';
import { ContactFormV2Component } from '../../../../contacts/components/contact-form-v2/contact-form-v2.component';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  EMPTY_GUID,
  EntityFormOption,
  fromApiDate,
  isBlank,
  numberOrNull,
  optionId,
  serverErrorMessage,
  toApiDate,
} from '../../../../finance/entity-form/entity-form.utils';
import {
  REAL_STATE_DEFAULT_COEFFICIENT,
  REAL_STATE_DEFAULT_PERIOD,
  REAL_STATE_USAGE_OPTIONS,
  RealStateInvestmentFormDialogData,
  RealStateInvestmentProviderLookup,
  realStateTotal,
  roundMoney,
} from '../../models/real-state-investment.model';
import { RealStateInvestmentTaxModel } from '../../models/summary-real-state-investment.model';
import { RealStateInvestmentEntityService } from '../../services/real-state-investment-entity.service';
import { RealStateInvestmentFormLookupsFacade } from '../../services/real-state-investment-form-lookups.facade';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { MasterService } from '../../../../master/services/master.service';

type FormErrors = Partial<Record<
  'provider' | 'account' | 'investmentDescription' | 'investmentDate' | 'usage' | 'maximumLinearCoefficient' | 'maximumPeriod'
  | 'residualValue' | 'base' | 'purchaseValueConstruction' | 'landPurchaseValue' | 'cadastralReference' | 'cadastralValue'
  | 'cadastralValueConstruction' | 'cadastralValueLand', string>>;

const DESCRIPTION_MAX = 150;
const CADASTRAL_REFERENCE_MAX = 20;
const OPTIONAL_AMOUNTS: (keyof FormErrors)[] = [
  'residualValue', 'purchaseValueConstruction', 'landPurchaseValue', 'cadastralValue', 'cadastralValueConstruction', 'cadastralValueLand',
];

@Component({
  selector: 'app-real-state-investment-form-dialog',
  templateUrl: './real-state-investment-form-dialog.component.html',
  styleUrls: ['./real-state-investment-form-dialog.component.scss'],
  standalone: false,
})
export class RealStateInvestmentFormDialogComponent implements OnInit, OnDestroy {
  readonly usageOptions = REAL_STATE_USAGE_OPTIONS;
  readonly descriptionMax = DESCRIPTION_MAX;
  readonly cadastralReferenceMax = CADASTRAL_REFERENCE_MAX;

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: FormErrors = {};

  accounts: EntityFormOption[] = [];
  providers: EntityFormOption[] = [];
  taxes: RealStateInvestmentTaxModel[] = [];
  taxesConfig: any = { label: 'Impuestos', placeholder: 'Buscar impuesto…', options: [], groupBy: 'group' };
  reloadingProviders = false;

  isProjectEnable = false;
  projects: EntityFormOption[] = [];

  readonly amortizationTypeControl = new UntypedFormControl({ value: 'Tabla AEAT', disabled: true });

  fileUid: string | null = null;
  isImage = false;
  isPdf = false;
  loadingFile = false;
  imageUrl: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  private loaded: InvestmentPropertyModel | null = null;
  private objectUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: RealStateInvestmentFormDialogData): Observable<boolean> {
    return dialog.open<RealStateInvestmentFormDialogComponent, RealStateInvestmentFormDialogData, boolean>(RealStateInvestmentFormDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<RealStateInvestmentFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: RealStateInvestmentFormDialogData,
    private entityService: RealStateInvestmentEntityService,
    private lookups: RealStateInvestmentFormLookupsFacade,
    private addFileService: AddFileService,
    private sanitizer: DomSanitizer,
    private notify: NotificationService,
    private activityService: ActivityService,
    private masterService: MasterService,
  ) {
    this.form = this.fb.group({
      provider: [null],
      account: [null],
      project: [null],
      investmentDescription: [''],
      investmentDate: [null],
      usage: [null],
      maximumLinearCoefficient: [REAL_STATE_DEFAULT_COEFFICIENT],
      maximumPeriod: [REAL_STATE_DEFAULT_PERIOD],
      residualValue: [null],
      base: [null],
      purchaseValueConstruction: [null],
      landPurchaseValue: [null],
      cadastralReference: [''],
      cadastralValue: [null],
      cadastralValueConstruction: [null],
      cadastralValueLand: [null],
      companyPremisesOrOffice: [false],
      taxIds: [[]],
    });
  }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get title(): string {
    return this.isEdit ? 'Editar inversión inmobiliaria' : 'Nueva inversión inmobiliaria';
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return [this.loaded.investmentDescription, this.loaded.contactsName].filter(Boolean).join(' · ') || 'Modifica los datos de la inversión';
    }
    return 'Registra la compra de un inmueble, su amortización y sus valores catastrales';
  }

  get providerControl(): UntypedFormControl {
    return this.form.get('provider') as UntypedFormControl;
  }

  get accountControl(): UntypedFormControl {
    return this.form.get('account') as UntypedFormControl;
  }

  get investmentDescriptionControl(): UntypedFormControl {
    return this.form.get('investmentDescription') as UntypedFormControl;
  }

  get investmentDateControl(): UntypedFormControl {
    return this.form.get('investmentDate') as UntypedFormControl;
  }

  get usageControl(): UntypedFormControl {
    return this.form.get('usage') as UntypedFormControl;
  }

  get companyPremisesControl(): UntypedFormControl {
    return this.form.get('companyPremisesOrOffice') as UntypedFormControl;
  }

  get maximumLinearCoefficientControl(): UntypedFormControl {
    return this.form.get('maximumLinearCoefficient') as UntypedFormControl;
  }

  get maximumPeriodControl(): UntypedFormControl {
    return this.form.get('maximumPeriod') as UntypedFormControl;
  }

  get residualValueControl(): UntypedFormControl {
    return this.form.get('residualValue') as UntypedFormControl;
  }

  get purchaseValueConstructionControl(): UntypedFormControl {
    return this.form.get('purchaseValueConstruction') as UntypedFormControl;
  }

  get landPurchaseValueControl(): UntypedFormControl {
    return this.form.get('landPurchaseValue') as UntypedFormControl;
  }

  get cadastralReferenceControl(): UntypedFormControl {
    return this.form.get('cadastralReference') as UntypedFormControl;
  }

  get cadastralValueControl(): UntypedFormControl {
    return this.form.get('cadastralValue') as UntypedFormControl;
  }

  get cadastralValueConstructionControl(): UntypedFormControl {
    return this.form.get('cadastralValueConstruction') as UntypedFormControl;
  }

  get cadastralValueLandControl(): UntypedFormControl {
    return this.form.get('cadastralValueLand') as UntypedFormControl;
  }

  get baseControl(): UntypedFormControl {
    return this.form.get('base') as UntypedFormControl;
  }

  get taxesControl(): UntypedFormControl {
    return this.form.get('taxIds') as UntypedFormControl;
  }

  get hasFile(): boolean {
    return !!this.fileUid && this.fileUid !== EMPTY_GUID;
  }

  get selectedTaxes(): RealStateInvestmentTaxModel[] {
    const selected: EntityFormOption[] = this.form.get('taxIds').value || [];
    return selected.map(option => this.taxes.find(tax => tax.id === Number(option.id))).filter(Boolean) as RealStateInvestmentTaxModel[];
  }

  get baseAmount(): number {
    return roundMoney(Number(this.form.get('base').value) || 0);
  }

  get total(): number {
    return realStateTotal(this.baseAmount, this.selectedTaxes);
  }

  get taxAmount(): number {
    return roundMoney(this.total - this.baseAmount);
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.revokePreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  newProvider(): void {
    const scratch = { isProvider: true, isClient: false, isPhysical: true };
    ContactFormV2Component.open(this.dialog, { scratch }).subscribe(result => {
      if (!result) return;
      this.reloadingProviders = true;
      this.lookups.providers()
        .then(list => {
          this.providers = this.providerOptions(list);
          const uid = result.uid || result.id;
          const created = this.providers.find(option => this.sameId(option.id, uid))
            || this.providers.find(option => !!result.companyName && option.display === result.companyName);
          if (created) this.providerControl.setValue(created);
        })
        .catch(() => this.notify.error('No se pudo recargar la lista de proveedores'))
        .then(() => { this.reloadingProviders = false; });
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const name = file.name.toLowerCase();
    const pdf = file.type === 'application/pdf' || name.endsWith('.pdf');
    const image = /\.(png|jpe?g)$/.test(name) || file.type === 'image/png' || file.type === 'image/jpeg';
    if (!pdf && !image) {
      this.notify.warning('Solo se admiten archivos PDF, PNG o JPG.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    this.loadingFile = true;
    this.addFileService.attachFile(formData, consts.INVESTMENT_ENTITY)
      .then(response => {
        if (!response || response.error) {
          this.notify.error(response && response.message ? response.message : 'No se pudo subir el archivo.');
          return;
        }
        LocalDB.RemoveForFiles();
        this.fileUid = response.uid;
        this.isPdf = pdf;
        this.isImage = !pdf;
        this.showPreview(file);
      })
      .catch(error => this.notify.error(serverErrorMessage(error, 'No se pudo subir el archivo.')))
      .then(() => { this.loadingFile = false; });
  }

  pickFromFiles(): void {
    this.dialog.open(ConboxDialogComponent, {
      width: '80%',
      height: '80%',
      maxWidth: '96vw',
      data: { originEntity: consts.INVESTMENT_ENTITY, filePath: consts.INVESTMENT_FILE_PATH },
    }).afterClosed().subscribe(result => {
      if (!result || !result.uid) return;
      const pdf = result.type === consts.TYPE_FILE_PDF;
      const image = result.type === consts.TYPE_FILE_IMAGE;
      if (!pdf && !image) {
        this.notify.warning('Solo se pueden adjuntar imágenes o PDF.');
        return;
      }

      this.loadingFile = true;
      const request = pdf
        ? this.addFileService.getImageFromFilesPDF(result.uid, consts.INVESTMENT_ENTITY)
        : this.addFileService.getImageFromFiles(result.uid, consts.INVESTMENT_ENTITY);

      request
        .then(blob => {
          this.fileUid = result.uid;
          this.isPdf = pdf;
          this.isImage = image;
          this.showPreview(blob);
        })
        .catch(() => this.notify.error('No se pudo cargar el documento seleccionado.'))
        .then(() => { this.loadingFile = false; });
    });
  }

  removeFile(): void {
    this.fileUid = null;
    this.isImage = false;
    this.isPdf = false;
    this.revokePreview();
  }

  save(): void {
    if (this.saving || this.loadingFile) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const request = this.isEdit ? this.entityService.update(this.data.uid as string, payload) : this.entityService.create(payload);

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(this.isEdit ? 'Inversión inmobiliaria actualizada correctamente' : 'Inversión inmobiliaria creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = serverErrorMessage(error, this.isEdit
          ? 'Ha ocurrido un error inesperado al actualizar la inversión'
          : 'Ha ocurrido un error inesperado al crear la inversión');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  hasError(field: keyof FormErrors): boolean {
    return this.submitted && !!this.errors[field];
  }

  private load(): void {
    this.ready = false;
    const record: Promise<InvestmentPropertyModel | null> = this.isEdit ? this.entityService.get(this.data.uid as string) : Promise.resolve(null);

    Promise.all([record, this.lookups.loadAll(), this.activityService.loadData(ActualSelected.activitySelected)])
      .then(([investment, lookups, activity]) => {
        this.accounts = (lookups.accounts || []).map(account => ({ id: account.number, display: `${account.number} - ${account.name}` }));
        this.providers = this.providerOptions(lookups.providers);
        this.setTaxes(lookups.taxes || []);
        this.loaded = investment;
        this.isProjectEnable = !!(activity && activity.isProject);

        const projectsPromise = this.isProjectEnable
          ? firstValueFrom(this.masterService.getProjects())
          : Promise.resolve<EntityFormOption[]>([]);

        return projectsPromise.then(projects => {
          this.projects = projects || [];
          if (investment) this.applyRecord(investment);
          this.ready = true;
        });
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(investment: InvestmentPropertyModel): void {
    if (investment.accountingModelsNumber && !this.accounts.some(option => option.id === investment.accountingModelsNumber)) {
      this.accounts = [...this.accounts, { id: investment.accountingModelsNumber, display: investment.accountingModelsNumber }];
    }
    if (investment.contactsUid && investment.contactsUid !== EMPTY_GUID && !this.providers.some(option => this.sameId(option.id, investment.contactsUid))) {
      this.providers = [...this.providers, { id: investment.contactsUid, display: investment.contactsName || 'Proveedor' }];
    }
    const missing = (investment.taxes || [])
      .filter(tax => !this.taxes.some(item => item.id === Number(tax.id)))
      .map(tax => ({ id: Number(tax.id), name: tax.name, taxImport: Number(tax.taxImport) || 0, type: tax.type }));
    if (missing.length) this.setTaxes([...this.taxes, ...missing]);

    this.form.patchValue({
      provider: this.providers.find(option => this.sameId(option.id, investment.contactsUid)) || null,
      account: this.accounts.find(option => option.id === investment.accountingModelsNumber) || null,
      investmentDescription: investment.investmentDescription || '',
      investmentDate: fromApiDate(investment.investmentDate),
      usage: this.usageOptions.find(option => option.id === Number(investment.usage)) || null,
      maximumLinearCoefficient: numberOrNull(investment.maximumLinearCoefficient),
      maximumPeriod: numberOrNull(investment.maximumPeriod),
      residualValue: numberOrNull(investment.residualValue),
      base: numberOrNull(investment.base),
      purchaseValueConstruction: numberOrNull(investment.purchaseValueConstruction),
      landPurchaseValue: numberOrNull(investment.landPurchaseValue),
      cadastralReference: investment.cadastralReference || '',
      cadastralValue: numberOrNull(investment.cadastralValue),
      cadastralValueConstruction: numberOrNull(investment.cadastralValueConstruction),
      cadastralValueLand: numberOrNull(investment.cadastralValueLand),
      companyPremisesOrOffice: !!investment.companyPremisesOrOffice,
      taxIds: this.taxOptionsFor((investment.taxes || []).map(tax => Number(tax.id))),
      project: this.projects.find(option => this.sameId(option.id, investment.projectUid)) || null,
    }, { emitEvent: false });

    this.fileUid = investment.filesInvestmentUid && investment.filesInvestmentUid !== EMPTY_GUID ? investment.filesInvestmentUid : null;
    this.isImage = !!investment.isImage;
    this.isPdf = !!investment.isPdf;
    if (this.hasFile && (this.isImage || this.isPdf)) {
      this.loadingFile = true;
      const request = this.isPdf
        ? this.addFileService.getImageFromFilesPDF(this.fileUid as string, consts.INVESTMENT_ENTITY)
        : this.addFileService.getImageFromFiles(this.fileUid as string, consts.INVESTMENT_ENTITY);
      request
        .then(blob => this.showPreview(blob))
        .catch(() => this.notify.warning('No se pudo cargar la vista previa del documento.'))
        .then(() => { this.loadingFile = false; });
    }
  }

  private validate(): FormErrors {
    const value = this.form.getRawValue();
    const errors: FormErrors = {};

    if (optionId(value.provider) === null) errors.provider = 'Selecciona el proveedor';
    if (optionId(value.account) === null) errors.account = 'Selecciona el tipo de inversión';
    if (isBlank(value.investmentDescription)) errors.investmentDescription = 'Indica la descripción';
    else if (String(value.investmentDescription).trim().length > DESCRIPTION_MAX) errors.investmentDescription = `Máximo ${DESCRIPTION_MAX} caracteres`;
    if (!value.investmentDate) errors.investmentDate = 'Indica la fecha de compra';
    if (!optionId(value.usage)) errors.usage = 'Selecciona el uso';
    if (this.invalidSmallInteger(value.maximumLinearCoefficient)) errors.maximumLinearCoefficient = 'Indica un número entero entre 0 y 100';
    if (this.invalidSmallInteger(value.maximumPeriod)) errors.maximumPeriod = 'Indica un número entero de años entre 0 y 100';
    if (isBlank(value.base) || isNaN(Number(value.base))) errors.base = 'Indica el total sin impuestos';
    else if (Number(value.base) <= 0) errors.base = 'El total sin impuestos debe ser mayor que 0';
    if (!isBlank(value.cadastralReference) && String(value.cadastralReference).trim().length > CADASTRAL_REFERENCE_MAX) {
      errors.cadastralReference = `Máximo ${CADASTRAL_REFERENCE_MAX} caracteres`;
    }
    OPTIONAL_AMOUNTS.forEach(field => {
      const amount = value[field];
      if (!isBlank(amount) && (isNaN(Number(amount)) || Number(amount) < 0)) errors[field] = 'Importe no válido';
    });

    return errors;
  }

  private buildPayload(): InvestmentPropertyModel {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    const provider = value.provider;
    const taxes = this.selectedTaxes.map(tax => ({ id: tax.id, name: tax.name, taxImport: tax.taxImport, type: tax.type }));

    return {
      ...base,
      accountingModelsId: 0,
      accountingModelsNumber: optionId(value.account),
      contactsUid: optionId(provider),
      contactsName: provider && provider.display ? provider.display : '',
      filesInvestmentUid: this.hasFile ? this.fileUid : EMPTY_GUID,
      isImage: this.hasFile && this.isImage,
      isPdf: this.hasFile && this.isPdf,
      investmentDate: toApiDate(value.investmentDate) as any,
      investmentDescription: String(value.investmentDescription).trim(),
      usage: Number(optionId(value.usage)),
      maximumLinearCoefficient: Number(value.maximumLinearCoefficient),
      maximumPeriod: Number(value.maximumPeriod),
      residualValue: this.amount(value.residualValue),
      base: this.amount(value.base),
      purchaseValueConstruction: this.amount(value.purchaseValueConstruction),
      landPurchaseValue: this.amount(value.landPurchaseValue),
      cadastralReference: String(value.cadastralReference || '').trim(),
      cadastralValue: this.amount(value.cadastralValue),
      cadastralValueConstruction: this.amount(value.cadastralValueConstruction),
      cadastralValueLand: this.amount(value.cadastralValueLand),
      companyPremisesOrOffice: !!value.companyPremisesOrOffice,
      taxes,
      total: realStateTotal(this.amount(value.base), taxes),
      manualMovement: null as any,
      projectUid: this.isProjectEnable ? optionId(value.project) : null,
    };
  }

  private setTaxes(list: RealStateInvestmentTaxModel[]): void {
    this.taxes = list || [];
    this.taxesConfig = {
      ...this.taxesConfig,
      options: this.taxes.map(tax => ({ id: tax.id, display: tax.name, group: tax.type || 'Otros' })),
    };
  }

  private taxOptionsFor(ids: number[]): any[] {
    return (this.taxesConfig.options || []).filter((option: any) => ids.indexOf(Number(option.id)) !== -1);
  }

  private providerOptions(list: RealStateInvestmentProviderLookup[]): EntityFormOption[] {
    return (list || []).map(provider => ({ id: provider.uid, display: provider.companyName || 'Sin nombre' }));
  }

  private amount(value: any): number {
    return isBlank(value) || isNaN(Number(value)) ? 0 : roundMoney(Number(value));
  }

  private invalidSmallInteger(value: any): boolean {
    if (isBlank(value) || isNaN(Number(value))) return true;
    const number = Number(value);
    return !Number.isInteger(number) || number < 0 || number > 100;
  }

  private sameId(a: any, b: any): boolean {
    return !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();
  }

  private showPreview(blob: Blob): void {
    this.revokePreview();
    if (!blob) return;
    const source = this.isPdf && blob.type !== 'application/pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
    this.objectUrl = URL.createObjectURL(source);
    if (this.isPdf) {
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    } else {
      this.imageUrl = this.objectUrl;
    }
  }

  private revokePreview(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.imageUrl = null;
    this.pdfUrl = null;
  }
}
